'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import {
  Briefcase,
  GraduationCap,
  Zap,
  Leaf,
  CircleDollarSign,
  Scale,
  HeartPulse,
  Home,
  Users,
  Tent,
  Shield,
  Trash2,
  Palette,
  Wifi,
  Bus,
  Sprout,
  Map,
  Droplets,
  Droplet,
  Wrench,
} from 'lucide-react';
import { fetchCategorias, type CategoriaApi } from '@/services/catalogos';
import {
  fetchResultadosPublicos,
  fetchIndicadoresVigentes,
  type ResultadoPublicoApi,
  type IndicadorVigenteApi,
} from '@/services/indicadores';

const getCategoryMeta = (name: string) => {
  const normName = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (normName.includes('econom'))
    return { icon: Briefcase, color: 'bg-[#3B82F6]', bg: 'bg-[#EFF6FF]', text: 'text-[#1D4ED8]' };
  if (normName.includes('educa'))
    return { icon: GraduationCap, color: 'bg-[#F59E0B]', bg: 'bg-[#FFFBEB]', text: 'text-[#B45309]' };
  if (normName.includes('energ'))
    return { icon: Zap, color: 'bg-[#EAB308]', bg: 'bg-[#FEF9C3]', text: 'text-[#A16207]' };
  if (normName.includes('ambient'))
    return { icon: Leaf, color: 'bg-[#10B981]', bg: 'bg-[#ECFDF5]', text: 'text-[#047857]' };
  if (normName.includes('finanz'))
    return {
      icon: CircleDollarSign,
      color: 'bg-[#059669]',
      bg: 'bg-[#D1FAE5]',
      text: 'text-[#065F46]',
    };
  if (normName.includes('gobernan'))
    return { icon: Scale, color: 'bg-[#64748B]', bg: 'bg-[#F8FAFC]', text: 'text-[#334155]' };
  if (normName.includes('salud'))
    return { icon: HeartPulse, color: 'bg-[#F43F5E]', bg: 'bg-[#FFF1F2]', text: 'text-[#BE123C]' };
  if (normName.includes('viviend'))
    return { icon: Home, color: 'bg-[#8B5CF6]', bg: 'bg-[#F5F3FF]', text: 'text-[#6D28D9]' };
  if (normName.includes('poblacion'))
    return { icon: Users, color: 'bg-[#EC4899]', bg: 'bg-[#FDF2F8]', text: 'text-[#BE185D]' };
  if (normName.includes('recrea'))
    return { icon: Tent, color: 'bg-[#F97316]', bg: 'bg-[#FFF7ED]', text: 'text-[#C2410C]' };
  if (normName.includes('segurid'))
    return { icon: Shield, color: 'bg-[#EF4444]', bg: 'bg-[#FEF2F2]', text: 'text-[#B91C1C]' };
  if (normName.includes('residuo'))
    return { icon: Trash2, color: 'bg-[#78716C]', bg: 'bg-[#F5F5F4]', text: 'text-[#44403C]' };
  if (normName.includes('deporte') || normName.includes('cultur'))
    return { icon: Palette, color: 'bg-[#D946EF]', bg: 'bg-[#FDF4FF]', text: 'text-[#A21CAF]' };
  if (normName.includes('telecom'))
    return { icon: Wifi, color: 'bg-[#0EA5E9]', bg: 'bg-[#F0F9FF]', text: 'text-[#0369A1]' };
  if (normName.includes('transpor'))
    return { icon: Bus, color: 'bg-[#6366F1]', bg: 'bg-[#EEF2FF]', text: 'text-[#4338CA]' };
  if (normName.includes('agricultur'))
    return { icon: Sprout, color: 'bg-[#84CC16]', bg: 'bg-[#ECFCCB]', text: 'text-[#4D7C0F]' };
  if (normName.includes('planificac'))
    return { icon: Map, color: 'bg-[#14B8A6]', bg: 'bg-[#F0FDFA]', text: 'text-[#0F766E]' };
  if (normName.includes('aguas residu'))
    return { icon: Droplets, color: 'bg-[#94A3B8]', bg: 'bg-[#F8FAFC]', text: 'text-[#475569]' };
  if (normName.includes('agua'))
    return { icon: Droplet, color: 'bg-[#06B6D4]', bg: 'bg-[#ECFEFF]', text: 'text-[#0E7490]' };

  return { icon: Wrench, color: 'bg-[#64748B]', bg: 'bg-[#F8FAFC]', text: 'text-[#334155]' };
};

const ISO_THEMES = [
  'Economía',
  'Educación',
  'Energía',
  'Ambiente y cambio climático',
  'Finanzas',
  'Gobernanza',
  'Salud',
  'Viviendas',
  'Población y condiciones sociales',
  'Recreación',
  'Seguridad',
  'Residuos sólidos',
  'Deporte y cultura',
  'Telecomunicaciones',
  'Transporte',
  'Agricultura urbana y/o local y seguridad alimentaria',
  'Planificación urbana',
  'Aguas residuales',
  'Agua',
];

export default function CategoriasPage() {
  const [categories, setCategories] = useState<CategoriaApi[]>([]);
  const [results, setResults] = useState<ResultadoPublicoApi[]>([]);
  const [vigentes, setVigentes] = useState<IndicadorVigenteApi[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [cats, res, vig] = await Promise.all([
          fetchCategorias(),
          fetchResultadosPublicos().catch(() => []),
          fetchIndicadoresVigentes({ publicable: true }).catch(() => []),
        ]);
        if (active) {
          if (cats.length > 0) setCategories(cats);
          setResults(res);
          setVigentes(vig);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load categories explorer data', err);
        if (active) setLoading(false);
      }
    };
    void loadData();
    return () => {
      active = false;
    };
  }, []);

  const displayCategories = useMemo(() => {
    const baseList =
      categories.length > 0
        ? categories.map((c) => ({
            id: String(c.id),
            name: c.nombre,
            description:
              c.descripcion || `Fichas, trazabilidad e indicadores temáticos de ${c.nombre}.`,
          }))
        : ISO_THEMES.map((theme, index) => ({
            id: String(index + 1),
            name: theme,
            description: `Fichas, trazabilidad e indicadores temáticos de ${theme}.`,
          }));

    return baseList.map((item) => {
      const normCat = item.name.trim().toLowerCase();
      const uniqueCodes = new Set<string>();

      vigentes.forEach((v) => {
        if ((v.categoria || '').trim().toLowerCase() === normCat) {
          uniqueCodes.add(v.codigo_interno || String(v.indicador_id));
        }
      });

      results.forEach((r) => {
        if ((r.categoria || '').trim().toLowerCase() === normCat) {
          uniqueCodes.add(r.codigo_interno || r.indicador);
        }
      });

      const meta = getCategoryMeta(item.name);
      return {
        ...item,
        count: uniqueCodes.size,
        ...meta,
      };
    });
  }, [categories, vigentes, results]);

  return (
    <main className="max-w-7xl mx-auto w-full px-8 py-16 flex-1">
      <div className="mb-12">
        <h2 className="text-4xl font-[family-name:var(--font-righteous)] text-[#002B5B] mb-4">
          Explorar por Temas
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl">
          El Sistema de Indicadores de Ciudad Inteligente y Sostenible (SICIS) clasifica la
          información estratégica de Luján en áreas temáticas estandarizadas según la norma ISO
          37120.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-100 p-8 animate-pulse flex flex-col h-64"
            >
              <div className="w-14 h-14 rounded-2xl bg-gray-200 mb-6"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3 mt-auto"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayCategories.map((cat) => {
            const Icon = cat.icon;
            return (
              <Link href="/indicadores" key={cat.id} className="group">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 relative overflow-hidden hover:shadow-md transition-all flex flex-col h-full">
                  <div className={`absolute top-0 left-0 right-0 h-2 ${cat.color}`}></div>

                  <div
                    className={`w-14 h-14 rounded-2xl ${cat.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className={`w-7 h-7 ${cat.text}`} />
                  </div>

                  <h3 className="font-bold text-xl text-gray-800 mb-2">{cat.name}</h3>
                  <p className="text-gray-500 text-sm mb-6 flex-1">{cat.description}</p>

                  <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-auto">
                    <span className="text-sm font-bold text-gray-500">
                      {cat.count} {cat.count === 1 ? 'indicador' : 'indicadores'}
                    </span>
                    <span
                      className={`text-sm font-bold ${cat.text} flex items-center group-hover:translate-x-1 transition-transform`}
                    >
                      Explorar <span className="ml-1">→</span>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
